import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';
import * as TFF from '@tff/types/tff';
import { useRebook } from '../../RebookContext';
import TFFTextInput from '../../../../styled-components/TFFTextInput';
import TFFSelectInput from '../../../../styled-components/TFFSelectInput';
import TFFTypography from '../../../../styled-components/TFFTypography';
import TFFIcon from '../../../../styled-components/TFFIcon';

interface NdcEditSegmentsProps {
  orderDetails: TFF.OrderDetails;
  journey: TFF.Journey;
  setLowerClass: (value: boolean) => void;
  setSelectedCabinClass: (value: string) => void;
  selectedCabinClass: string;
}

const CABIN_CLASSES = [{ value: 'Economy' }, { value: 'Premium' }, { value: 'Business' }, { value: 'First' }];

const NdcEditSegments: React.FC<NdcEditSegmentsProps> = ({
  orderDetails,
  journey,
  setLowerClass,
  setSelectedCabinClass,
  selectedCabinClass,
}) => {
  const capitalizeFirstLetter = (str?: string) => (str ? str[0].toUpperCase() + str.slice(1).toLowerCase() : '');
  const segments = journey.Segments;
  const journeyType = journey.Ond === orderDetails.Journeys[0].Ond ? 'Outbound' : 'Return';
  const [departure, setDeparture] = useState(format(new Date(segments[0].DepartureTime), 'yyyy-MM-dd'));
  const [origin, setOrigin] = useState(segments[0].Origin);
  const [destination, setDestination] = useState(segments[segments.length - 1].Destination);
  const [selectedValue, setSelectedValue] = useState(capitalizeFirstLetter(journey.CabinClassName));
  const intl = useIntl();
  const { toggleSearchButton, toggleSearchState } = useRebook();

  const handleCabinChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value;
    setSelectedValue(newValue);
    setSelectedCabinClass(newValue);

    const cabinIndex = CABIN_CLASSES.findIndex(i => i.value.toUpperCase() === journey.CabinClassName?.toUpperCase());
    const selectedIndex = CABIN_CLASSES.findIndex(i => i.value === newValue);
    setLowerClass(cabinIndex > selectedIndex);
  };

  useEffect(() => {
    if (selectedCabinClass) {
      setSelectedValue(selectedCabinClass);
    }
  }, [selectedCabinClass]);

  const handleOrigin = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrigin(e.target.value.toUpperCase().replaceAll(/[^A-Z]/g, ''));
    toggleSearchButton(true);
  };

  const handleDestination = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDestination(e.target.value.toUpperCase().replaceAll(/[^A-Z]/g, ''));
    toggleSearchButton(true);
  };

  const handleDeparture = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDeparture(e.target.value);
    toggleSearchButton(true);
  };

  useEffect(() => {
    const currentCarrierCode = segments[0].MarketingCarrier;
    const currentFareBase = journey.FareBasis ?? 'UNKNOWN';
    const fareProductsIndex = journeyType === 'Outbound' ? 0 : 1;
    const paxTypes = Object.values(orderDetails.Passengers).map(p => p.Type);
    const counts: Record<string, number> = {};
    for (const type of paxTypes) {
      counts[type] = (counts[type] || 0) + 1;
    }
    const { INF = 0, ADT = 0, CHD = 0 } = counts;

    const fareAmount: number = orderDetails.Fares
      ? Object.values(orderDetails.Fares).reduce<number>(
          (sum, f) => sum + ((f as TFF.Fare).FareProducts[fareProductsIndex]?.BaseAmount ?? 0),
          0,
        )
      : 0;

    const newTravelDetails = [
      departure,
      origin,
      destination,
      `${INF}`,
      `${ADT}`,
      `${CHD}`,
      currentCarrierCode,
      currentFareBase,
      `${fareAmount}`,
      'false',
      `${selectedValue}`,
    ];
    toggleSearchState(journeyType, newTravelDetails);
  }, [departure, origin, destination, selectedValue, toggleSearchState, journeyType]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '16px',
        gap: '16px',
      }}
    >
      <TFFTextInput
        inputId={`ndc-origin-${journeyType}`}
        labelText={intl.formatMessage({ id: 'rebook.Origin' })}
        labelColor="color-theme-variant"
        leadingIconName="aircraft-up"
        value={origin}
        onChange={handleOrigin}
      />
      <TFFTextInput
        inputId={`ndc-departure-${journeyType}`}
        labelText={intl.formatMessage({ id: 'rebook.Departure' })}
        labelColor="color-theme-variant"
        leadingIconName="calendar"
        inputType="date"
        value={departure}
        onChange={handleDeparture}
      />
      <TFFTextInput
        inputId={`ndc-destination-${journeyType}`}
        labelText={intl.formatMessage({ id: 'rebook.Destination' })}
        labelColor="color-theme-variant"
        leadingIconName="aircraft-down"
        value={destination}
        onChange={handleDestination}
      />
      <div>
        <TFFSelectInput
          id={`ndc-cabin-class-${journeyType}`}
          label={intl.formatMessage({ id: 'rebook.cabinClass' })}
          labelColor="color-theme-variant"
          value={selectedValue}
          onChange={handleCabinChange}
        >
          {CABIN_CLASSES.map(option => (
            <option key={option.value} value={option.value}>
              {option.value}
            </option>
          ))}
        </TFFSelectInput>
        {selectedCabinClass && selectedCabinClass !== '' && (
          <div style={{ display: 'flex', gap: '4px', marginTop: '8px', maxWidth: '200px' }}>
            <TFFIcon iconName="letter-i-circled" size="xsmall" color="color-theme-variant" />
            <TFFTypography text={intl.formatMessage({ id: 'rebook.ndc.sameCabinClass' })} size="xsmall" />
          </div>
        )}
      </div>
    </div>
  );
};

export default NdcEditSegments;
